import { useCallback, useRef, type MouseEventHandler, type PointerEventHandler } from "react";

const LONG_PRESS_MS = 500;
const MOVE_THRESHOLD_PX = 10;

type UseLongPressOptions = {
  onLongPress?: () => void;
  onTap?: () => void;
  disabled?: boolean;
};

export function useLongPress({ onLongPress, onTap, disabled = false }: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const suppressClickRef = useRef(false);
  const handledTapViaPointerRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cancelGesture = useCallback(() => {
    clearTimer();
    startRef.current = null;
    pointerIdRef.current = null;
  }, [clearTimer]);

  const onPointerDown: PointerEventHandler = useCallback(
    (event) => {
      if (disabled || event.button !== 0) return;

      suppressClickRef.current = false;
      pointerIdRef.current = event.pointerId;
      startRef.current = { x: event.clientX, y: event.clientY };

      clearTimer();
      if (onLongPress) {
        timerRef.current = setTimeout(() => {
          suppressClickRef.current = true;
          onLongPress();
        }, LONG_PRESS_MS);
      }
    },
    [clearTimer, disabled, onLongPress],
  );

  const onPointerMove: PointerEventHandler = useCallback(
    (event) => {
      if (!startRef.current || pointerIdRef.current !== event.pointerId) return;

      const dx = event.clientX - startRef.current.x;
      const dy = event.clientY - startRef.current.y;
      if (Math.hypot(dx, dy) > MOVE_THRESHOLD_PX) {
        cancelGesture();
      }
    },
    [cancelGesture],
  );

  const onPointerUp: PointerEventHandler = useCallback(
    (event) => {
      if (pointerIdRef.current !== event.pointerId) return;

      const wasLongPress = suppressClickRef.current;
      clearTimer();
      startRef.current = null;
      pointerIdRef.current = null;

      if (!wasLongPress && !disabled) {
        handledTapViaPointerRef.current = true;
        onTap?.();
      }
    },
    [clearTimer, disabled, onTap],
  );

  const onPointerCancel: PointerEventHandler = useCallback(
    (event) => {
      if (pointerIdRef.current !== event.pointerId) return;
      suppressClickRef.current = false;
      cancelGesture();
    },
    [cancelGesture],
  );

  const onClick: MouseEventHandler = useCallback((event) => {
    if (suppressClickRef.current) {
      event.preventDefault();
      event.stopPropagation();
      suppressClickRef.current = false;
      return;
    }
    if (handledTapViaPointerRef.current) {
      handledTapViaPointerRef.current = false;
      event.preventDefault();
      return;
    }
    if (!disabled) {
      onTap?.();
    }
  }, [disabled, onTap]);

  const onContextMenu: MouseEventHandler = useCallback((event) => {
    event.preventDefault();
  }, []);

  return {
    longPressHandlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onPointerLeave: onPointerCancel,
      onClick,
      onContextMenu,
    },
  };
}
